// File validation and helper utilities

export const MAX_FILE_COUNT = 10; // Maximum 10 files per message

// Size limits by category
export const SIZE_LIMITS = {
  images: 10 * 1024 * 1024, // 10MB
  documents: 10 * 1024 * 1024, // 10MB
  data: 10 * 1024 * 1024, // 10MB
} as const;

// File types organized by category (flat arrays to match backend)
export const ALLOWED_TYPES = {
  images: ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'],
  documents: [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    'text/markdown',
    'text/html',
  ],
  data: [
    'text/csv',
    'application/json',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
  ],
} as const;

// Extensions where browsers report empty MIME type or application/octet-stream
const ALLOWED_EXTENSIONS_BY_CATEGORY: Partial<Record<FileCategory, readonly string[]>> = {
  documents: ['jsx'],
} as const;

type FileCategory = keyof typeof ALLOWED_TYPES;

export function getFileCategory(contentType: string, filename?: string): FileCategory | null {
  for (const [category, types] of Object.entries(ALLOWED_TYPES)) {
    if ((types as readonly string[]).includes(contentType)) {
      return category as FileCategory;
    }
  }

  if (filename) {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext) {
      for (const [category, exts] of Object.entries(ALLOWED_EXTENSIONS_BY_CATEGORY)) {
        if ((exts as readonly string[]).includes(ext)) {
          return category as FileCategory;
        }
      }
    }
  }

  return null;
}

// Get size limit based on content type
export function getFileSizeLimit(contentType: string): number {
  const category = getFileCategory(contentType);
  return category ? SIZE_LIMITS[category] : SIZE_LIMITS.documents;
}

export interface FileValidationError {
  file: File;
  error: string;
}

export function validateFile(file: File): string | null {
  const category = getFileCategory(file.type, file.name);
  if (!category) {
    return 'File type not supported. Please upload images (PNG, JPG, GIF, WebP, SVG), documents (PDF, DOCX, TXT, MD, HTML, JSX), or data files (CSV, JSON, XLSX, XLS).';
  }

  // Check file size with type-specific limit
  const sizeLimit = SIZE_LIMITS[category];
  if (file.size > sizeLimit) {
    return `File size must be less than ${formatFileSize(sizeLimit)}`;
  }

  return null;
}

export function validateFiles(files: File[]): {
  valid: File[];
  invalid: FileValidationError[];
} {
  const valid: File[] = [];
  const invalid: FileValidationError[] = [];

  files.forEach((file) => {
    const error = validateFile(file);
    if (error) {
      invalid.push({ file, error });
    } else {
      valid.push(file);
    }
  });

  return { valid, invalid };
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export function isImage(contentType: string): boolean {
  return getFileCategory(contentType) === 'images';
}

// File icons by category
const FILE_ICONS: Record<FileCategory, string> = {
  images: '🖼️',
  documents: '📄',
  data: '📊',
};

export function getFileIcon(contentType: string): string {
  const category = getFileCategory(contentType);
  return category ? FILE_ICONS[category] : '📎';
}

export function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

export async function createImagePreview(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!isImage(file.type)) {
      reject(new Error('File is not an image'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        resolve(e.target.result as string);
      } else {
        reject(new Error('Failed to read file'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export function handleDragEvent(
  e: React.DragEvent,
  onFiles?: (files: File[]) => void
): void {
  e.preventDefault();
  e.stopPropagation();

  if (e.type === 'drop' && onFiles) {
    const files = Array.from(e.dataTransfer.files);
    onFiles(files);
  }
}
